import React, { useState } from "react";
import { Badge, Button, Card, IndexTable } from "@shopify/polaris";
import StorePageFrame from "../userCustomDashboard/emLayout/StorePageFrame";
import SyncEngineHealthPanel from "../support/SyncEngineHealthPanel";
import {
  formatCount,
  formatDateTime,
  readableStatus,
  statusTone,
} from "../support/supportFormatters";
import { useStoreWorkspace } from "./StoreWorkspaceContext";
import WorkspaceState from "./WorkspaceState";
import { workspaceModel } from "./workspaceModel";
import "./style.css";

const StatusBadge = ({ status }) => <Badge status={statusTone(status)}>{readableStatus(status)}</Badge>;
const enabledStatus = (value) => value === true ? "ACTIVE" : value === false ? "INACTIVE" : "UNKNOWN";
export const disputeSyncStatus = (account = {}) => {
  const connectionHealth = String(account.connectionHealth || "").toUpperCase();
  if (["DISPUTE_SYNC_STALLED", "DISPUTE_SYNC_ERROR"].includes(connectionHealth)) {
    return connectionHealth;
  }
  const syncError = String(account.disputesSyncErrorCode || "").toUpperCase();
  if (connectionHealth === "RATE_LIMITED" || syncError === "PAYPAL_RATE_LIMITED") return "RATE_LIMITED";
  if (connectionHealth === "SYNCING" || syncError === "PAYPAL_SYNC_PARTIAL") return "SYNCING";
  if (syncError) return "DISPUTE_SYNC_ERROR";
  if (account.disputesSyncStartedAt) {
    const startedAt = Date.parse(account.disputesSyncStartedAt);
    const completedAt = Date.parse(account.disputesLastSyncedAt);
    if (!Number.isFinite(completedAt) || (Number.isFinite(startedAt) && startedAt > completedAt)) {
      return "SYNCING";
    }
  }
  return account.disputesLastSyncedAt ? "HEALTHY" : "NOT_STARTED";
};

const StoreConfiguration = () => {
  const workspace = useStoreWorkspace();
  const [selectedMerchantAccountId, setSelectedMerchantAccountId] = useState(null);
  const model = workspaceModel(workspace.data, workspace.shopId);
  const { configuration, paypalAccounts, carrierRules, connections, disputes } = model;
  const paypalConnection = connections.paypal || {};
  const accountRows = paypalAccounts.map((account, index) => {
    const merchantAccountId = account?.payerId || account?.merchantAccountId || null;
    const selected = merchantAccountId === selectedMerchantAccountId;
    return (
      <IndexTable.Row id={String(account?.id || index)} key={String(account?.id || index)} position={index}>
        <IndexTable.Cell>
          {account?.description || account?.payerEmail || account?.merchantAccountId || "PayPal account"}
          {account?.payerId ? <span className="store-support-cell-note">Payer {account.payerId}</span> : null}
        </IndexTable.Cell>
        <IndexTable.Cell><StatusBadge status={account?.connectionHealth || account?.status || enabledStatus(account?.usable ?? account?.active)} /></IndexTable.Cell>
        <IndexTable.Cell>{account?.defaultAccount === true ? "Default" : "—"}</IndexTable.Cell>
        <IndexTable.Cell>
          {formatDateTime(account?.connectedAt)}
          <span className="store-support-cell-note">Token expires {formatDateTime(account?.tokenExpiresAt)}</span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge status={disputeSyncStatus(account)} />
          <span className="store-support-cell-note">Last synced {formatDateTime(account?.disputesLastSyncedAt)}</span>
          {account?.disputesSyncErrorCode ? <span className="store-support-cell-note">Error {account.disputesSyncErrorCode}</span> : null}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Button
            size="slim"
            disabled={!merchantAccountId}
            onClick={() => setSelectedMerchantAccountId(selected ? null : merchantAccountId)}
            ariaPressed={selected}
          >
            {selected ? "Hide sync health" : "View sync health"}
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });
  const ruleRows = carrierRules.map((rule, index) => (
    <IndexTable.Row id={String(rule?.id || index)} key={String(rule?.id || index)} position={index}>
      <IndexTable.Cell>{rule?.originalCarrier || rule?.carrierToReplaceId || "Not recorded"}</IndexTable.Cell>
      <IndexTable.Cell>{rule?.countryName || rule?.destinationCountry || rule?.country || "Global"}</IndexTable.Cell>
      <IndexTable.Cell>{rule?.paypalCarrierName || rule?.paypalCarrierCode || rule?.paypalCarrier || rule?.carrier || rule?.paypalEnumerated || "Not recorded"}</IndexTable.Cell>
      <IndexTable.Cell>{rule?.referenceNote || rule?.reason || "—"}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <StorePageFrame title="Configuration" maxWidth="1400px">
      <WorkspaceState
        loading={workspace.loading}
        refreshing={workspace.refreshing}
        error={workspace.error}
        hasData={Boolean(workspace.data)}
        onRetry={() => workspace.refresh()}
      >
        <Card>
          <div className="store-support-card">
            <div className="store-support-heading"><h2>Effective sync configuration</h2><p>Read-only values currently applied by PalSync.</p></div>
            <dl className="store-support-definition-grid store-support-definition-grid--four">
              <div className="store-support-definition"><dt>Order sync</dt><dd><StatusBadge status={enabledStatus(configuration.syncEnabled)} /></dd></div>
              <div className="store-support-definition"><dt>Digital orders</dt><dd><StatusBadge status={enabledStatus(configuration.digitalOrdersEnabled ?? configuration.digitalSync)} /></dd></div>
              <div className="store-support-definition"><dt>Carrier mapping</dt><dd><StatusBadge status={enabledStatus(configuration.carrierMappingEnabled ?? configuration.courierMapping)} /></dd></div>
              <div className="store-support-definition"><dt>Carrier matcher</dt><dd><StatusBadge status={enabledStatus(configuration.courierMatcher)} /></dd></div>
              <div className="store-support-definition"><dt>Notify buyer</dt><dd><StatusBadge status={enabledStatus(configuration.notifyBuyer)} /></dd></div>
              <div className="store-support-definition"><dt>Webhook</dt><dd><StatusBadge status={configuration.webhookStatus || "UNKNOWN"} /></dd></div>
              <div className="store-support-definition"><dt>Carrier rules</dt><dd>{formatCount(configuration.carrierRuleCount ?? carrierRules.length)}</dd></div>
            </dl>
          </div>
        </Card>
        <Card>
          <div className="store-support-card store-support-connection-summary">
            <div>
              <div className="store-support-heading__with-status">
                <h2>PayPal connection readiness</h2>
                <StatusBadge status={paypalConnection.connectionHealth || paypalConnection.status} />
              </div>
              <p>
                {formatCount(paypalConnection.activeAccounts)} usable · {formatCount(paypalConnection.unhealthyAccounts)} unhealthy · disputes {readableStatus(disputes.syncStatus)}
              </p>
              {paypalConnection.rateLimited ? (
                <p className="store-support-cell-note">
                  PayPal has temporarily rate limited dispute updates. PalSync has paused the dispute safety-net retry; real-time order tracking sync remains enabled and unaffected.
                </p>
              ) : null}
            </div>
          </div>
          <div className="store-support-table-heading"><div><h2>PayPal accounts</h2><p>All accounts returned by the workspace, including rate-limit state.</p></div></div>
          <IndexTable resourceName={{ singular: "account", plural: "accounts" }} itemCount={paypalAccounts.length} selectable={false} headings={[
            { title: "Support-safe account" }, { title: "Connection" }, { title: "Default" }, { title: "Connected / renewal" }, { title: "Dispute sync" }, { title: "Sync evidence" },
          ]}>{accountRows}</IndexTable>
          {paypalAccounts.length === 0 ? <div className="store-support-table-empty">No PayPal accounts were returned.</div> : null}
        </Card>
        {selectedMerchantAccountId ? (
          <SyncEngineHealthPanel
            shopId={workspace.shopId}
            merchantAccountId={selectedMerchantAccountId}
          />
        ) : null}
        <Card>
          <div className="store-support-table-heading"><div><h2>Carrier mapping rules</h2><p>Country-specific PayPal carrier mappings currently in effect.</p></div></div>
          <IndexTable resourceName={{ singular: "rule", plural: "rules" }} itemCount={carrierRules.length} selectable={false} headings={[
            { title: "Original carrier" }, { title: "Destination country" }, { title: "PayPal carrier" }, { title: "Reference" },
          ]}>{ruleRows}</IndexTable>
          {carrierRules.length === 0 ? <div className="store-support-table-empty">No carrier mapping rules were returned.</div> : null}
        </Card>
      </WorkspaceState>
    </StorePageFrame>
  );
};

export default StoreConfiguration;
